import { lazy, Suspense } from 'react';
import { useTranslation } from 'react-i18next';

import { OpButton } from 'new-components/DLS/OpButton/OpButton';

import type { RequestPermissionButtonProps } from './RequestPermissionButton';

// Lazy load the RequestPermissionButton component to reduce initial bundle size
// This component and its dependencies (OpForm, OpModal, etc.) will only be loaded
// when an H-4000 error occurs, not on initial page load
const RequestPermissionButton = lazy(() =>
  import('./RequestPermissionButton').then((module) => ({
    default: module.RequestPermissionButton,
  })),
);

/**
 * Lazy-loaded wrapper for RequestPermissionButton.
 *
 * This component lazy-loads RequestPermissionButton to reduce initial bundle size.
 * The component and its heavy dependencies (OpForm, OpModal, etc.) are only loaded
 * when needed (when an H-4000 error occurs).
 *
 * @param props - Same props as RequestPermissionButton
 */
export const LazyRequestPermissionButton = (
  props: RequestPermissionButtonProps,
) => {
  const { t } = useTranslation();

  return (
    <Suspense
      fallback={
        <OpButton
          key="request-permission-loading"
          testId="request-permission-button-fallback"
          type="primary"
          size="small"
          loading
          disabled
        >
          {t('Request permission')}
        </OpButton>
      }
    >
      <RequestPermissionButton {...props} />
    </Suspense>
  );
};
